import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import PetCard from "@/components/Profile/PetCard";
import CreatePetModal from "@/components/Profile/CreatePetModal";
import { Button } from "@/components/ui/button";
import { Plus, Heart, Sparkles } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface Pet {
  id: string;
  name: string;
  species: string;
  breed: string | null;
  age: number | null;
  gender: string | null;
  description: string | null;
  avatar_url: string | null;
  created_at: string;
  is_public: boolean;
  user_id: string;
  posts_count: number;
}

const Pets = () => {
  const [pets, setPets] = useState<Pet[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();
  
  // Set document title and meta description for SEO
  useEffect(() => {
    document.title = 'I Miei Pet - Gestisci i Tuoi Animali | AstroPet';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Gestisci i profili dei tuoi animali domestici su AstroPet. Aggiungi cani, gatti e altri pet, crea il loro passaporto digitale e condividi momenti speciali con la community.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Gestisci i profili dei tuoi animali domestici su AstroPet. Aggiungi cani, gatti e altri pet, crea il loro passaporto digitale e condividi momenti speciali con la community.';
      document.head.appendChild(meta);
    }
    
    // Set canonical URL
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', 'https://astropet.it/pets');
    } else {
      const link = document.createElement('link');
      link.rel = 'canonical';
      link.href = 'https://astropet.it/pets';
      document.head.appendChild(link);
    }
  }, []);

  const fetchPets = async () => {
    if (!user) return;

    try {
      // Query pets with posts count
      const { data, error } = await supabase
        .from('pets')
        .select(`
          *,
          posts_count:posts(count)
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Database error:', error);
        setPets([]);
      } else {
        // Transform the data to match our interface
        const petsWithCount = (data || []).map(pet => ({
          ...pet,
          posts_count: pet.posts_count?.[0]?.count || 0
        }));
        setPets(petsWithCount);
      }
    } catch (error) {
      console.error('Error fetching pets:', error);
      setPets([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPets();
  }, [user]);

  const handlePetCreated = () => {
    setIsCreateModalOpen(false);
    fetchPets();
    toast({
      title: "Pet aggiunto!",
      description: "Il tuo nuovo amico è stato aggiunto con successo",
    });
  };

  const handlePetDeleted = () => {
    fetchPets();
    toast({
      title: "Pet rimosso",
      description: "Il pet è stato rimosso dal tuo profilo",
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen glass">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-8">
            <div className="h-8 bg-white/10 rounded-2xl w-64"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-80 bg-white/10 rounded-2xl"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen glass">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-2xl gradient-primary flex items-center justify-center glow-primary">
              <Heart className="w-6 h-6 text-white" fill="white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold gradient-text font-space">I miei Pet</h1>
              <p className="text-muted-foreground">
                {pets.length} {pets.length === 1 ? 'amico peloso' : 'amici pelosi'}
              </p>
            </div>
          </div>

          <Button
            onClick={() => setIsCreateModalOpen(true)}
            className="gradient-primary text-white hover:opacity-90 glow-primary transition-all hover:scale-105"
          >
            <Plus className="w-5 h-5 mr-2" />
            Aggiungi Pet
          </Button>
        </div>

        {/* Pets Grid */}
        {pets.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full glass border border-primary/20 flex items-center justify-center">
              <Sparkles className="w-12 h-12 text-primary/60" />
            </div>
            <h3 className="text-2xl font-bold text-foreground mb-4">Nessun pet ancora</h3>
            <p className="text-muted-foreground mb-8 max-w-md mx-auto">
              Aggiungi il tuo primo amico peloso per iniziare a condividere momenti speciali con la community!
            </p>
            <Button
              onClick={() => setIsCreateModalOpen(true)}
              className="gradient-primary text-white hover:opacity-90 glow-primary transition-all hover:scale-105"
            >
              <Plus className="w-5 h-5 mr-2" />
              Aggiungi il tuo primo Pet
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pets.map((pet) => (
              <PetCard
                key={pet.id}
                pet={pet}
                showActions={true}
              />
            ))}
          </div>
        )}

        {/* Create Pet Modal */}
        <CreatePetModal
          open={isCreateModalOpen}
          onOpenChange={setIsCreateModalOpen}
          onSuccess={handlePetCreated}
        />
      </div>
    </div>
  );
};

export default Pets;